import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import FindingForm from "./components/FindingForm";
import GenerationDrawer from "./components/GenerationDrawer";
import ReportDocument from "./components/ReportDocument";
import ReportHeader from "./components/ReportHeader";
import ReportToc from "./components/ReportToc";
import { manualReportsApi as api } from "../../../services/manualReportsApi";
import { copyRichText, downloadBlobFile, downloadTextFile } from "./utils/clipboard";
import {
  buildFindingHtml,
  buildFindingPlainText,
  buildFullReportHtml,
  buildFullReportHtmlDocument,
  buildFullReportPlainText,
  buildReportOutline,
  buildSectionHtml,
} from "./utils/formatFindingHtml";
import { buildReportDocxBlob } from "./utils/reportDocx";
import "./manualReports.css";

const EMPTY_FINDING = {
  id: null,
  name: "",
  severity: "Medium",
  location: "",
  description: "",
  issue_notes: "",
  generated_definition: "",
  generated_details: "",
  generated_remediation: "",
  final_definition: "",
  final_details: "",
  final_remediation: "",
};

function createEmptyFinding() {
  return { ...EMPTY_FINDING };
}

function upsertFindingInReport(report, finding) {
  const existing = report.findings.find((entry) => entry.id === finding.id);
  const findings = existing
    ? report.findings.map((entry) => (entry.id === finding.id ? finding : entry))
    : [...report.findings, finding];
  return { ...report, findings };
}

export default function ManualReportWorkspacePage() {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState("report");
  const [draftFinding, setDraftFinding] = useState(createEmptyFinding);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [generationInstructions, setGenerationInstructions] = useState("");
  const [generationTarget, setGenerationTarget] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingReport, setIsSavingReport] = useState(false);
  const [isSavingFinding, setIsSavingFinding] = useState(false);
  const [isReorderingFindings, setIsReorderingFindings] = useState(false);
  const [findingGenerationState, setFindingGenerationState] = useState({ findingId: null, target: "" });
  const [isGeneratingExecutiveSummary, setIsGeneratingExecutiveSummary] = useState(false);
  const [isGeneratingMethodology, setIsGeneratingMethodology] = useState(false);

  const loadWorkspace = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");
      const reportData = await api.getReport(reportId);
      setReport(reportData);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setIsLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const tocSections = useMemo(() => {
    if (!report) {
      return [];
    }

    return buildReportOutline(report).navigationEntries;
  }, [report]);

  const isGeneratingDrawerFinding =
    isDrawerOpen &&
    findingGenerationState.target === "full" &&
    (findingGenerationState.findingId === draftFinding.id ||
      (draftFinding.id == null && findingGenerationState.findingId == null));

  function getFindingById(findingId) {
    return report?.findings.find((finding) => finding.id === findingId) || null;
  }

  function handleReportFieldChange(field, value) {
    setReport((currentReport) => ({ ...currentReport, [field]: value }));
  }

  function handleFindingFieldChange(findingId, field, value) {
    setReport((currentReport) => ({
      ...currentReport,
      findings: currentReport.findings.map((finding) =>
        finding.id === findingId
          ? {
              ...finding,
              [field]: value,
              ...(field === "description" ? { issue_notes: value } : {}),
            }
          : finding,
      ),
    }));
  }

  function handleDrawerFindingFieldChange(field, value) {
    setDraftFinding((currentFinding) => ({
      ...currentFinding,
      [field]: value,
      ...(field === "description" ? { issue_notes: value } : {}),
    }));
  }

  function upsertFindingLocally(finding) {
    setReport((currentReport) => upsertFindingInReport(currentReport, finding));
    setDraftFinding((currentDraft) => (currentDraft.id === finding.id ? { ...createEmptyFinding(), ...finding } : currentDraft));
  }

  function reorderFindings(findings, findingId, targetIndex) {
    const currentIndex = findings.findIndex((finding) => finding.id === findingId);
    if (currentIndex === -1 || targetIndex < 0 || targetIndex >= findings.length || currentIndex === targetIndex) {
      return findings;
    }

    const nextFindings = [...findings];
    const [movedFinding] = nextFindings.splice(currentIndex, 1);
    nextFindings.splice(targetIndex, 0, movedFinding);

    return nextFindings.map((finding, index) => ({
      ...finding,
      sort_order: index + 1,
    }));
  }

  async function persistFinding(findingInput) {
    const payload = { ...findingInput };
    delete payload.id;
    delete payload.report_id;
    delete payload.created_at;
    delete payload.updated_at;
    payload.description = findingInput.description ?? findingInput.issue_notes ?? "";

    if (findingInput.id) {
      return api.updateFinding(findingInput.id, payload);
    }

    return api.createFinding(report.id, payload);
  }

  async function handleSaveReport(options = {}) {
    const { silent = false, reportOverride = null } = options;
    try {
      setIsSavingReport(true);
      setError("");
      const updated = await api.updateReport(report.id, reportOverride || report);
      setReport(updated);
      if (!silent) {
        setStatusMessage("Report metadata saved.");
      }
      return updated;
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setIsSavingReport(false);
    }
  }

  async function handleSaveFinding(findingIdOrOptions, maybeOptions = {}) {
    const findingId = typeof findingIdOrOptions === "number" ? findingIdOrOptions : null;
    const options = findingId ? maybeOptions : findingIdOrOptions || {};
    const { closeDrawer = false, silent = false } = options;
    const targetFinding = findingId ? getFindingById(findingId) : draftFinding;

    if (!targetFinding) {
      return undefined;
    }

    try {
      setIsSavingFinding(true);
      setError("");
      const savedFinding = await persistFinding(targetFinding);
      upsertFindingLocally(savedFinding);
      if (closeDrawer) {
        setIsDrawerOpen(false);
      }
      if (!silent) {
        setStatusMessage("Finding saved.");
      }
      return savedFinding;
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setIsSavingFinding(false);
    }
  }

  async function handleGenerateDrawerFinding() {
    const canGenerateDrawerFinding = Boolean(draftFinding.name && draftFinding.severity && draftFinding.location);
    if (!canGenerateDrawerFinding) {
      setError("Name, severity, and location are required before generation.");
      return;
    }

    try {
      setFindingGenerationState({ findingId: draftFinding.id, target: "full" });
      setError("");
      const savedFinding = await persistFinding(draftFinding);
      const generatedFinding = await api.generateFindingWithInstructions(savedFinding.id, {
        instructions: generationInstructions,
      });
      upsertFindingLocally(generatedFinding);
      setDraftFinding({ ...createEmptyFinding(), ...generatedFinding });
      setIsDrawerOpen(false);
      setStatusMessage("Finding draft generated.");
      return generatedFinding;
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setFindingGenerationState({ findingId: null, target: "" });
    }
  }

  async function handleGenerateFinding(findingId, options = {}) {
    const finding = getFindingById(findingId);
    if (!finding) {
      return undefined;
    }

    const { section = "full", instructions = "" } = options;
    const canGenerate = Boolean(finding.name && finding.severity && finding.location);
    if (!canGenerate) {
      setError("Name, severity, and location are required before generation.");
      return undefined;
    }

    try {
      setFindingGenerationState({ findingId, target: section });
      setError("");
      const savedFinding = await persistFinding(finding);
      const generatedFinding =
        section === "full"
          ? await api.generateFindingWithInstructions(savedFinding.id, { instructions })
          : await api.generateFindingSection(savedFinding.id, section, { instructions });
      upsertFindingLocally(generatedFinding);
      setStatusMessage(section === "full" ? "Finding refined." : `${section} regenerated.`);
      return generatedFinding;
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setFindingGenerationState({ findingId: null, target: "" });
    }
  }

  async function handleDeleteFinding(findingId) {
    if (!window.confirm("Delete this finding?")) {
      return;
    }

    try {
      setError("");
      await api.deleteFinding(findingId);
      setReport((currentReport) => ({
        ...currentReport,
        findings: currentReport.findings.filter((finding) => finding.id !== findingId),
      }));
      if (draftFinding.id === findingId) {
        setDraftFinding(createEmptyFinding());
        setIsDrawerOpen(false);
      }
      setStatusMessage("Finding deleted.");
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  async function handleMoveFinding(findingId, targetIndex) {
    if (!report) {
      return;
    }

    const reorderedFindings = reorderFindings(report.findings, findingId, targetIndex);
    if (reorderedFindings === report.findings) {
      return;
    }

    const previousFindings = report.findings;
    setReport((currentReport) => ({
      ...currentReport,
      findings: reorderedFindings,
    }));

    try {
      setIsReorderingFindings(true);
      setError("");
      const savedFindings = await Promise.all(
        reorderedFindings.map((finding) =>
          api.updateFinding(finding.id, {
            ...finding,
            description: finding.description ?? finding.issue_notes ?? "",
          }),
        ),
      );

      setReport((currentReport) => ({
        ...currentReport,
        findings: savedFindings,
      }));
      setDraftFinding((currentDraft) =>
        currentDraft.id
          ? savedFindings.find((finding) => finding.id === currentDraft.id) || currentDraft
          : currentDraft,
      );
      setStatusMessage("Finding order updated.");
    } catch (requestError) {
      setReport((currentReport) => ({
        ...currentReport,
        findings: previousFindings,
      }));
      setError(requestError.message);
    } finally {
      setIsReorderingFindings(false);
    }
  }

  function handleCreateNewFinding() {
    setDraftFinding(createEmptyFinding());
    setGenerationInstructions("");
    setIsDrawerOpen(true);
  }

  function handleOpenFindingDetails(findingId) {
    const finding = getFindingById(findingId);
    if (!finding) {
      return;
    }
    setDraftFinding({ ...createEmptyFinding(), ...finding });
    setGenerationInstructions("");
    setIsDrawerOpen(true);
  }

  async function handleGenerateExecutiveSummary() {
    try {
      setIsGeneratingExecutiveSummary(true);
      setError("");
      const updated = await api.generateExecutiveSummary(report.id);
      setReport(updated);
      setStatusMessage("Executive summary generated.");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setIsGeneratingExecutiveSummary(false);
    }
  }

  async function handleGenerateMethodology() {
    try {
      setIsGeneratingMethodology(true);
      setError("");
      const updated = await api.generateMethodology(report.id);
      setReport(updated);
      setStatusMessage("Methodology generated.");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setIsGeneratingMethodology(false);
    }
  }

  async function handleCopyFinding(findingId) {
    const finding = getFindingById(findingId);
    if (!finding) {
      return;
    }

    await copyRichText({
      html: buildFindingHtml(finding),
      text: buildFindingPlainText(finding),
    });
  }

  async function handleCopyExecutiveSummary() {
    await copyRichText({
      html: buildSectionHtml("Executive Summary", report.executive_summary || ""),
      text: `Executive Summary\n\n${report.executive_summary || ""}`,
    });
  }

  async function handleCopyMethodology() {
    await copyRichText({
      html: buildSectionHtml("Methodology", report.methodology_text || ""),
      text: `Methodology\n\n${report.methodology_text || ""}`,
    });
  }

  async function handleCopyAll() {
    await copyRichText({
      html: buildFullReportHtml(report),
      text: buildFullReportPlainText(report),
    });
  }

  function handleDownloadHtml() {
    const safeTitle = (report.report_title || "pentest-report")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    downloadTextFile(
      `${safeTitle || "pentest-report"}.html`,
      buildFullReportHtmlDocument(report),
      "text/html;charset=utf-8",
    );
  }

  async function handleDownloadWord() {
    const safeTitle = (report.report_title || "pentest-report")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    const blob = await buildReportDocxBlob(report);
    downloadBlobFile(`${safeTitle || "pentest-report"}.docx`, blob);
  }

  function openGenerationDrawer(findingId, target = "full") {
    const finding = getFindingById(findingId);
    if (!finding || !finding.name || !finding.severity || !finding.location) {
      setError("Name, severity, and location are required before generation.");
      return;
    }

    setGenerationTarget({ findingId, target });
    setGenerationInstructions("");
  }

  function closeGenerationDrawer() {
    setGenerationTarget(null);
    setGenerationInstructions("");
  }

  async function handleConfirmGeneration() {
    if (!generationTarget) {
      return;
    }

    const result = await handleGenerateFinding(generationTarget.findingId, {
      section: generationTarget.target,
      instructions: generationInstructions,
    });

    if (result) {
      closeGenerationDrawer();
    }
  }

  if (isLoading) {
    return (
      <main className="manual-reports-scope manual-report-shell workspace-shell">
        <p>Loading workspace...</p>
      </main>
    );
  }

  if (!report) {
    return (
      <main className="manual-reports-scope manual-report-shell workspace-shell">
        <p className="status-error">{error || "Report not found."}</p>
        <Link to="/reports/manual" className="secondary-button inline-link-button">
          Back to reports
        </Link>
      </main>
    );
  }

  return (
    <main className="manual-reports-scope manual-report-shell workspace-shell">
      <section className="panel workspace-header-panel">
        <div className="workspace-header-top">
          <div className="workspace-topbar">
            <Link to="/reports/manual" className="secondary-button inline-link-button">
              Back to Reports
            </Link>
            <button type="button" className="ghost-button" onClick={() => window.location.reload()}>
              Refresh
            </button>
          </div>
          <button type="button" className="primary-button" onClick={handleCreateNewFinding}>
            New Finding
          </button>
        </div>

        <div className="workspace-heading-row">
          <div>
            <p className="eyebrow">Report Workspace</p>
            <h1>{report.report_title || "Untitled Report"}</h1>
          </div>
          <div className="tab-row workspace-tab-row">
            {[
              ["report", "Report"],
              ["metadata", "Metadata"],
            ].map(([tabKey, label]) => (
              <button
                key={tabKey}
                type="button"
                className={`tab-button ${activeTab === tabKey ? "active" : ""}`}
                onClick={() => setActiveTab(tabKey)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {error ? <p className="status-error">{error}</p> : null}
      {statusMessage ? <p className="status-success">{statusMessage}</p> : null}

      {activeTab === "metadata" ? (
        <ReportHeader
          report={report}
          onChange={handleReportFieldChange}
          onSave={() => handleSaveReport()}
          isSaving={isSavingReport}
        />
      ) : null}

      {activeTab === "report" ? (
        <section className="report-workspace-layout">
          <ReportToc sections={tocSections} />

          <ReportDocument
            report={report}
            onReportFieldChange={handleReportFieldChange}
            onSaveReport={handleSaveReport}
            onGenerateExecutiveSummary={handleGenerateExecutiveSummary}
            onGenerateMethodology={handleGenerateMethodology}
            onCopyAll={handleCopyAll}
            onDownloadHtml={handleDownloadHtml}
            onDownloadWord={handleDownloadWord}
            onCopyExecutiveSummary={handleCopyExecutiveSummary}
            onCopyMethodology={handleCopyMethodology}
            isSavingReport={isSavingReport}
            isGeneratingExecutiveSummary={isGeneratingExecutiveSummary}
            isGeneratingMethodology={isGeneratingMethodology}
            onCreateNewFinding={handleCreateNewFinding}
            onOpenFindingDetails={handleOpenFindingDetails}
            onDeleteFinding={handleDeleteFinding}
            onMoveFinding={handleMoveFinding}
            onFindingFieldChange={handleFindingFieldChange}
            onSaveFinding={handleSaveFinding}
            onRequestGenerateFinding={(findingId) => openGenerationDrawer(findingId, "full")}
            onCopyFinding={handleCopyFinding}
            isSavingFinding={isSavingFinding}
            isReorderingFindings={isReorderingFindings}
            findingGenerationState={findingGenerationState}
          />
        </section>
      ) : null}

      <FindingForm
        isOpen={isDrawerOpen}
        finding={draftFinding}
        onChange={handleDrawerFindingFieldChange}
        onClose={() => setIsDrawerOpen(false)}
        onSave={() => handleSaveFinding({ closeDrawer: true })}
        onRequestGenerate={handleGenerateDrawerFinding}
        isSaving={isSavingFinding}
        isGenerating={isGeneratingDrawerFinding}
        canGenerate={Boolean(draftFinding.name && draftFinding.severity && draftFinding.location)}
      />

      <GenerationDrawer
        isOpen={Boolean(generationTarget)}
        title="Refine Full Finding"
        instructions={generationInstructions}
        onInstructionsChange={setGenerationInstructions}
        onClose={closeGenerationDrawer}
        onConfirm={handleConfirmGeneration}
        isGenerating={findingGenerationState.findingId === generationTarget?.findingId && findingGenerationState.target === generationTarget?.target}
      />
    </main>
  );
}
